import type { AlertKind } from './ui/types.ts';

/**
 * Opt-in background / Windows notifications for Human Review and failures.
 *
 * The in-page toast, title badge, favicon and chime already exist. This model is the extra
 * path that still fires when the dashboard tab is in the background — a Notification API
 * toast that Windows shows on the desktop. It is a different thing from the P5.4 tracker
 * comment: that tags the human on the ticket when a vendor CLI login expires. This does not
 * post to the tracker, and it never takes a CLI transcript.
 */

export type BackgroundNotificationKind = 'human-review' | 'failure';

type BackgroundNotifySkipReason =
  | 'disabled'
  | 'not-granted'
  | 'not-eligible'
  | 'opted-out-kind'
  | 'quiet-hours'
  | 'visible'
  | 'duplicate';

/**
 * Alerts about the factory rather than a ticket. They ride the failures toggle, and their toast
 * says what happened ("Claude Code login lost on ai-rig") instead of "<ticket> failed".
 */
const OPERATOR_ALERT_KINDS: readonly AlertKind[] = ['harness-login', 'runner-offline', 'backup-failed', 'offsite-backup-failed', 'disk-low'];

const BACKGROUND_FAILURE_KINDS: ReadonlySet<AlertKind> = new Set([
  'escalation',
  'bounce-cap',
  'failed-run',
  'orphaned-job',
  'risky-gate',
  ...OPERATOR_ALERT_KINDS,
]);

const MINUTES_PER_DAY = 24 * 60;
const CLOCK_RE = /^(?:[01]?\d|2[0-3]):[0-5]\d$/;
const BODY_MAX = 180;

/**
 * Tokens that must never leave the box. Job errors and agent headlines can carry CLI output,
 * which is where API keys and session cookies live.
 */
const SECRET_PATTERNS: readonly RegExp[] = [
  /\bsk-[A-Za-z0-9_-]{8,}/g,
  /\bghp_[A-Za-z0-9]{20,}/g,
  /\bglpat-[A-Za-z0-9_-]{20,}/g,
  /\bxox[baprs]-[A-Za-z0-9-]{10,}/g,
  /\bBearer\s+\S+/gi,
  /\b(?:api[_-]?key|token|secret|password|authorization)\s*[:=]\s*\S+/gi,
  /\beyJ[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
];

export interface BackgroundNotificationPrefs {
  /** Master switch. Off until the operator asks; an ops tool that toasts uninvited gets muted. */
  enabled: boolean;
  humanReview: boolean;
  failures: boolean;
  /** Minutes from local midnight. Null = no quiet window. Both ends must be set. */
  quietStartMinutes: number | null;
  quietEndMinutes: number | null;
  /** Do not raise an OS toast while the operator is looking at the dashboard. */
  suppressWhenVisible: boolean;
}

export const DEFAULT_BACKGROUND_NOTIFICATION_PREFS: BackgroundNotificationPrefs = {
  enabled: false,
  humanReview: true,
  failures: true,
  quietStartMinutes: null,
  quietEndMinutes: null,
  suppressWhenVisible: true,
};

export interface BackgroundNotifyTarget {
  id: string;
  kind: AlertKind;
  ticketKey: string;
  headline: string;
  /** For an operator alert, what happened; the toast's title. */
  ticketTitle?: string;
}

export interface BackgroundNotifyDecision {
  notify: boolean;
  reason: BackgroundNotifySkipReason | 'ok';
}

export interface BackgroundNotificationPayload {
  title: string;
  body: string;
  tag: string;
  kind: BackgroundNotificationKind;
}

export function backgroundKindForAlert(kind: AlertKind): BackgroundNotificationKind | null {
  if (kind === 'merge') return 'human-review';
  if (BACKGROUND_FAILURE_KINDS.has(kind)) return 'failure';
  return null;
}

export function parseClockMinutes(value: string): number | null {
  const trimmed = value.trim();
  if (!CLOCK_RE.test(trimmed)) return null;
  const [hoursText, minutesText] = trimmed.split(':');
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const total = hours * 60 + minutes;
  if (total < 0 || total >= MINUTES_PER_DAY) return null;
  return total;
}

export function formatClockMinutes(minutes: number): string {
  if (!Number.isInteger(minutes) || minutes < 0 || minutes >= MINUTES_PER_DAY) return '';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/**
 * Quiet if `now` (local clock) sits in [start, end). A window that starts later than it ends
 * wraps midnight, so 22:00–07:00 is quiet at 23:00 and at 02:00, not at 12:00.
 * Equal or missing ends mean "no quiet window".
 */
export function isInQuietHours(
  now: Date,
  startMinutes: number | null,
  endMinutes: number | null,
): boolean {
  if (startMinutes === null || endMinutes === null) return false;
  if (!Number.isInteger(startMinutes) || !Number.isInteger(endMinutes)) return false;
  if (startMinutes === endMinutes) return false;
  if (startMinutes < 0 || endMinutes < 0 || startMinutes >= MINUTES_PER_DAY || endMinutes >= MINUTES_PER_DAY) {
    return false;
  }
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (startMinutes < endMinutes) return minutes >= startMinutes && minutes < endMinutes;
  return minutes >= startMinutes || minutes < endMinutes;
}

export function parseBackgroundNotificationPrefs(raw: unknown): BackgroundNotificationPrefs {
  const source = unwrapPrefs(raw);
  return {
    enabled: readBoolean(source.enabled, DEFAULT_BACKGROUND_NOTIFICATION_PREFS.enabled),
    humanReview: readBoolean(source.humanReview, DEFAULT_BACKGROUND_NOTIFICATION_PREFS.humanReview),
    failures: readBoolean(source.failures, DEFAULT_BACKGROUND_NOTIFICATION_PREFS.failures),
    quietStartMinutes: readMinutes(source.quietStartMinutes ?? source.quietStart),
    quietEndMinutes: readMinutes(source.quietEndMinutes ?? source.quietEnd),
    suppressWhenVisible: readBoolean(
      source.suppressWhenVisible,
      DEFAULT_BACKGROUND_NOTIFICATION_PREFS.suppressWhenVisible,
    ),
  };
}

export function decideBackgroundNotification(input: {
  prefs: BackgroundNotificationPrefs;
  alertKind: AlertKind;
  alertId: string;
  alreadyNotified: ReadonlySet<string>;
  now: Date;
  dashboardVisible: boolean;
  permissionGranted: boolean;
}): BackgroundNotifyDecision {
  if (!input.prefs.enabled) return { notify: false, reason: 'disabled' };
  if (!input.permissionGranted) return { notify: false, reason: 'not-granted' };

  const kind = backgroundKindForAlert(input.alertKind);
  if (kind === null) return { notify: false, reason: 'not-eligible' };
  if (kind === 'human-review' && !input.prefs.humanReview) {
    return { notify: false, reason: 'opted-out-kind' };
  }
  if (kind === 'failure' && !input.prefs.failures) {
    return { notify: false, reason: 'opted-out-kind' };
  }
  if (isInQuietHours(input.now, input.prefs.quietStartMinutes, input.prefs.quietEndMinutes)) {
    return { notify: false, reason: 'quiet-hours' };
  }
  if (input.prefs.suppressWhenVisible && input.dashboardVisible) {
    return { notify: false, reason: 'visible' };
  }
  if (input.alreadyNotified.has(input.alertId)) return { notify: false, reason: 'duplicate' };
  return { notify: true, reason: 'ok' };
}

export function planBackgroundNotification(
  target: BackgroundNotifyTarget,
  input: {
    prefs: BackgroundNotificationPrefs;
    alreadyNotified: ReadonlySet<string>;
    now: Date;
    dashboardVisible: boolean;
    permissionGranted: boolean;
  },
): BackgroundNotificationPayload | null {
  const decision = decideBackgroundNotification({
    prefs: input.prefs,
    alertKind: target.kind,
    alertId: target.id,
    alreadyNotified: input.alreadyNotified,
    now: input.now,
    dashboardVisible: input.dashboardVisible,
    permissionGranted: input.permissionGranted,
  });
  if (!decision.notify) return null;
  const kind = backgroundKindForAlert(target.kind);
  if (kind === null) return null;
  if (OPERATOR_ALERT_KINDS.includes(target.kind)) {
    const title = sanitizeNotificationText(target.ticketTitle ?? '') || `${sanitizeNotificationText(target.ticketKey) || 'Nightshift'} needs attention`;
    return {
      title: clip(title, 80),
      body: sanitizeNotificationText(target.headline) || title,
      tag: target.id,
      kind,
    };
  }
  return formatBackgroundNotification({
    kind,
    ticketKey: target.ticketKey,
    headline: target.headline,
    dedupeKey: target.id,
  });
}

/**
 * Canned OS-toast copy. Callers pass classified facts (ticket key, alert kind, a headline)
 * and never stdout/stderr: those are where credentials live.
 */
export function formatBackgroundNotification(input: {
  kind: BackgroundNotificationKind;
  ticketKey: string;
  headline?: string;
  dedupeKey?: string;
}): BackgroundNotificationPayload {
  const key = sanitizeNotificationText(input.ticketKey) || 'ticket';
  const title = input.kind === 'human-review' ? `${key} needs your merge` : `${key} failed`;
  const canned =
    input.kind === 'human-review' ? `${key} is in Human Review.` : `${key} has a failed stage.`;
  const extra = input.headline ? sanitizeNotificationText(input.headline) : '';
  const body = usableHeadline(extra, key) ? clip(`${canned} ${extra}`, BODY_MAX) : canned;
  return {
    title: clip(title, 80),
    body,
    tag: input.dedupeKey?.trim() || `${input.kind}:${key}`,
    kind: input.kind,
  };
}

export function sanitizeNotificationText(text: string): string {
  let next = text.replace(/\s+/g, ' ').trim();
  for (const pattern of SECRET_PATTERNS) {
    next = next.replace(pattern, '[redacted]');
  }
  next = next.replace(/\b(std(?:out|err))\b/gi, 'output');
  return clip(next, BODY_MAX);
}

/** Drop ids whose condition has cleared, so a later return of the same ticket can ping again. */
export function pruneNotifiedIds(
  notified: ReadonlySet<string>,
  liveIds: ReadonlySet<string>,
): Set<string> {
  const next = new Set<string>();
  for (const id of notified) {
    if (liveIds.has(id)) next.add(id);
  }
  return next;
}

function unwrapPrefs(raw: unknown): Record<string, unknown> {
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      return asRecord(parsed) ?? {};
    } catch {
      return {};
    }
  }
  return asRecord(raw) ?? {};
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readMinutes(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < MINUTES_PER_DAY) {
    return value;
  }
  if (typeof value === 'string') return parseClockMinutes(value);
  return null;
}

function usableHeadline(extra: string, key: string): boolean {
  if (!extra) return false;
  if (extra.includes('[redacted]')) return false;
  if (extra === key) return false;
  return true;
}

function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}
